import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Mail, Phone, MapPin, Linkedin, Twitter, Instagram } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const contactSchema = z.object({
  fullName: z.string().min(1, "Full name is required"),
  phoneNumber: z.string().optional(),
  email: z.string().email("Please enter a valid email address"),
  message: z.string().min(1, "Message is required"),
});

type ContactFormData = z.infer<typeof contactSchema>;

const Contact = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { toast } = useToast();

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      fullName: "",
      phoneNumber: "",
      email: "",
      message: "",
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsLoading(true);
    
    try {
      const { data: result, error } = await supabase.functions.invoke('contact-submit', {
        body: {
          fullName: data.fullName,
          email: data.email,
          phoneNumber: data.phoneNumber,
          message: data.message,
        },
      });

      if (error) {
        console.error("Function invocation error:", error);
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive",
        });
        return;
      }

      if (result?.success) {
        setShowSuccessModal(true);
        form.reset();
        toast({
          title: "Message sent!",
          description: "We'll get back to you within 24 hours.",
        });
      } else {
        toast({
          title: "Error",
          description: result?.error || "Failed to send message. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-accent/10">
      <Navigation />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">
              Contact <span className="gradient-text">Us</span>
            </h1>
            <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-6 max-w-3xl mx-auto border border-primary/20">
              <p className="text-lg text-muted-foreground">
                We'd love to hear from you. Whether you're a customer, partner, or investor, 
                our team is here to help you navigate your financial journey.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 p-8 rounded-2xl border border-primary/20">
                <h2 className="text-2xl font-heading font-semibold mb-6 gradient-text">Get in Touch</h2>
                
                <div className="space-y-6">
                  <div className="bg-background/50 p-4 rounded-xl border border-primary/10">
                    <h3 className="font-semibold mb-3 text-primary">Customers & Families</h3>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                        <Mail className="w-5 h-5 text-primary" />
                      </div>
                      <a href="mailto:hector@flexaura.me" className="text-primary hover:underline font-medium transition-colors">
                        hector@flexaura.me
                      </a>
                    </div>
                  </div>
                  
                  <div className="bg-background/50 p-4 rounded-xl border border-primary/10">
                    <h3 className="font-semibold mb-3 text-primary">Partnerships & Providers</h3>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                        <Mail className="w-5 h-5 text-primary" />
                      </div>
                      <a href="mailto:hector@flexaura.me" className="text-primary hover:underline font-medium transition-colors">
                        hector@flexaura.me
                      </a>
                    </div>
                  </div>
                  
                  <div className="bg-background/50 p-4 rounded-xl border border-primary/10">
                    <h3 className="font-semibold mb-3 text-primary">Investors</h3>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                        <Mail className="w-5 h-5 text-primary" />
                      </div>
                      <a href="mailto:hector@flexaura.me" className="text-primary hover:underline font-medium transition-colors">
                        hector@flexaura.me
                      </a>
                    </div>
                  </div>
                  
                  <div className="bg-background/50 p-4 rounded-xl border border-primary/10">
                    <h3 className="font-semibold mb-3 text-primary">Headquarters</h3>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-primary" />
                      </div>
                      <p className="text-muted-foreground font-medium">Dubai, United Arab Emirates</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-accent/10 via-primary/10 to-accent/10 p-6 rounded-xl border border-accent/20">
                <h3 className="font-heading font-semibold mb-4 gradient-text text-center">Follow Us</h3>
                <div className="flex gap-4 justify-center">
                  <a href="#" className="w-12 h-12 bg-background/60 rounded-lg flex items-center justify-center hover:bg-background/80 transition-all duration-300 border border-primary/10 hover:border-primary/30">
                    <Linkedin className="w-5 h-5 text-primary" />
                  </a>
                  <a href="#" className="w-12 h-12 bg-background/60 rounded-lg flex items-center justify-center hover:bg-background/80 transition-all duration-300 border border-primary/10 hover:border-primary/30">
                    <Twitter className="w-5 h-5 text-primary" />
                  </a>
                  <a href="#" className="w-12 h-12 bg-background/60 rounded-lg flex items-center justify-center hover:bg-background/80 transition-all duration-300 border border-primary/10 hover:border-primary/30">
                    <Instagram className="w-5 h-5 text-primary" />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 p-8 rounded-2xl border border-primary/20">
              <h2 className="text-2xl font-heading font-semibold mb-6 gradient-text">Send us a message</h2>
              <p className="text-muted-foreground mb-6">
                Fill in the form below and our team will get back to you within 24 hours.
              </p>
              
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="fullName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter your full name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phoneNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter your phone number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address *</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="Enter your email address" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Query / Message *</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Tell us how we can help you..."
                            className="min-h-[120px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button 
                    type="submit" 
                    className="w-full"
                    disabled={isLoading}
                  >
                    {isLoading ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </Form>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Message Sent!</DialogTitle>
            <DialogDescription className="text-center">
              Your message has been sent to our team. One of our reps will be in contact with you shortly.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button onClick={() => setShowSuccessModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contact;